import { useCallback } from "react";
import { createFileRoute } from "@tanstack/react-router";

import { RequireAuth } from "@/components/RequireAuth";
import { DocsBrowser, type DocsTab } from "@/components/portal/docs/DocsBrowser";

/**
 * Standalone Help page (`/portal/help`) — the header Help drawer's "Open in a
 * new tab" target, so an article can sit beside the portal at whatever size
 * the browser window allows. Escapes the `/portal` layout (the `portal_`
 * prefix strips layout nesting, not the URL path), same pattern as the report
 * and Spaces widget window routes.
 *
 * Needs only the bearer token: the Control Plane filters the docs index and
 * resources by the caller's tier, exactly as it does for the drawer.
 */
type HelpSearch = { slug?: string; tab?: DocsTab };

export const Route = createFileRoute("/portal_/help")({
  component: HelpPage,
  validateSearch: (search: Record<string, unknown>): HelpSearch => ({
    slug: typeof search.slug === "string" ? search.slug : undefined,
    tab: search.tab === "resources" ? "resources" : undefined,
  }),
});

function HelpPage() {
  const { slug, tab } = Route.useSearch();
  const navigate = Route.useNavigate();

  // Keep the URL on the article being read, so the tab can be bookmarked or shared.
  const handleViewChange = useCallback(
    (nextSlug: string | null, nextTab: DocsTab) => {
      navigate({
        search: {
          slug: nextSlug ?? undefined,
          tab: nextTab === "resources" ? "resources" : undefined,
        },
        replace: true,
      });
    },
    [navigate],
  );
  return (
    <RequireAuth>
      <div className="flex h-dvh flex-col bg-white">
        <header className="border-b px-4 py-3">
          <h1 className="text-lg font-semibold text-slate-900">Larasoft Help</h1>
          <p className="text-sm text-muted-foreground">Guides and resources for the portal.</p>
        </header>
        <DocsBrowser
          active
          initialSlug={slug}
          initialTab={tab ?? "help"}
          onViewChange={handleViewChange}
        />
      </div>
    </RequireAuth>
  );
}
